import { type DependencyList, useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

import { dependencyListKey } from '@erp-core/utils/dependency-key';

import type { ShortcutRegistry } from './shortcut-registry';
import { useShortcutManagerContext } from './ShortcutManagerContext';
import type { ModifierKey, ShortcutDefinition, ShortcutHelpGroup, ShortcutInput } from './types';

// ─── Hook ID generation ───────────────────────────────────────────────────────

let _hookIdSeq = 0;
const genHookId = () => `sc-hook-${++_hookIdSeq}`;

// ─── Stable empty array for server snapshot ───────────────────────────────────

const EMPTY_SHORTCUTS: readonly ShortcutDefinition[] = [];
const EMPTY_GROUPS: readonly ShortcutHelpGroup[] = [];
const EMPTY_DEPS: DependencyList = [];

// ─────────────────────────────────────────────────────────────────────────────
// useShortcutManager
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the raw ShortcutRegistry. Use this as an escape hatch when you
 * need imperative control (e.g. enable/disable from outside a component).
 *
 * @example
 * const registry = useShortcutManager();
 * registry.disable('my-shortcut-id');
 */
export function useShortcutManager(): ShortcutRegistry {
  return useShortcutManagerContext().registry;
}

// ─────────────────────────────────────────────────────────────────────────────
// useRegisterShortcut
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Register a single keyboard shortcut for the lifetime of the component.
 * The shortcut is automatically unregistered on unmount.
 *
 * The `handler` is always called via a stable ref — it can be a new function
 * reference every render without triggering re-registration.
 *
 * Only the combo/scope/scopeId/priority/enabled fields participate in
 * re-registration when they change. Pass extra deps in the `deps` array for
 * values that should cause re-registration.
 *
 * @returns The stable shortcut ID. Use it to enable/disable imperatively.
 *
 * @example
 * // Save form on Ctrl+S
 * const id = useRegisterShortcut({
 *   combo: { key: 's', modifiers: ['ctrl'] },
 *   scope: 'form',
 *   handler: handleSave,
 *   meta: { label: 'Save', group: 'Form' },
 * });
 *
 * @example
 * // Disable the shortcut while a mutation is in-flight
 * const id = useRegisterShortcut({ ... });
 * useShortcutEnabled(id, !isMutating);
 */
export function useRegisterShortcut(
  input: ShortcutInput,
  deps: DependencyList = EMPTY_DEPS,
): string {
  const { registry } = useShortcutManagerContext();

  // Stable ID for this hook instance — generated once, never changes
  const idRef = useRef<string>(input.id ?? genHookId());

  // Keep the latest handler in a ref so re-registration is NOT needed
  // when only the handler function reference changes (e.g. arrow functions
  // defined inline in the component body).
  const handlerRef = useRef(input.handler);
  handlerRef.current = input.handler;
  const inputRef = useRef(input);
  inputRef.current = input;

  // Serialize only the fields that affect the shortcut behaviour (not handler)

  const modifiersKey = JSON.stringify(input.combo.modifiers ?? []);
  const depsKey = dependencyListKey(deps);

  useEffect(() => {
    const id = idRef.current;
    const currentInput = inputRef.current;

    // The stable wrapper always calls the latest handler — no stale closure.
    const stableHandler = (event: KeyboardEvent): void => {
      handlerRef.current(event);
    };

    registry.register({
      ...currentInput,
      id,
      handler: stableHandler,
    });

    return () => {
      registry.unregister(id);
    };
  }, [
    registry,
    input.combo.key,
    modifiersKey,
    input.scope,
    input.scopeId,
    input.priority,
    input.enabled,
    depsKey,
  ]);

  return idRef.current;
}

// ─────────────────────────────────────────────────────────────────────────────
// useRegisterShortcuts
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Register a static set of shortcuts at once. Useful when a component owns
 * multiple shortcuts that share the same lifecycle.
 *
 * The `inputs` array is expected to be STABLE across renders (defined outside
 * the component or memoized). If the array reference changes, the shortcuts
 * are re-registered. Use `deps` to control re-registration timing explicitly.
 *
 * Handlers are always called via stable refs — safe to define inline.
 *
 * @example
 * useRegisterShortcuts([
 *   { combo: { key: 's', modifiers: ['ctrl'] }, scope: 'form', handler: save,   meta: { label: 'Save'   } },
 *   { combo: { key: 'Escape' },                 scope: 'form', handler: cancel, meta: { label: 'Cancel' } },
 * ]);
 */
export function useRegisterShortcuts(
  inputs: readonly ShortcutInput[],
  deps: DependencyList = EMPTY_DEPS,
): void {
  const { registry } = useShortcutManagerContext();
  const inputsRef = useRef(inputs);
  inputsRef.current = inputs;

  // Stable handler refs — one slot per input, updated every render
  const handlersRef = useRef<readonly ShortcutInput['handler'][]>([]);
  handlersRef.current = inputs.map((i) => i.handler);

  // Stable IDs for each slot — generated once per element position
  const idsRef = useRef<string[]>([]);
  while (idsRef.current.length < inputs.length) {
    idsRef.current.push(genHookId());
  }

  // Serialized combo keys for dep comparison (avoids object identity issues)

  const comboKey = JSON.stringify(
    inputs.map((i) => ({
      key: i.combo.key,
      modifiers: i.combo.modifiers ?? [],
      scope: i.scope,
      scopeId: i.scopeId,
      priority: i.priority,
      enabled: i.enabled,
    })),
  );
  const depsKey = dependencyListKey(deps);

  useEffect(() => {
    const registeredIds: string[] = [];
    inputsRef.current.forEach((input, idx) => {
      const id = idsRef.current[idx] ?? genHookId();
      const capturedIdx = idx;

      const stableHandler = (event: KeyboardEvent): void => {
        handlersRef.current[capturedIdx]?.(event);
      };

      registry.register({ ...input, id, handler: stableHandler });
      registeredIds.push(id);
    });

    return () => {
      registry.unregisterAll(registeredIds);
    };
  }, [registry, comboKey, depsKey]);
}

// ─────────────────────────────────────────────────────────────────────────────
// useShortcutScope
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Activate a named scope instance while the component is mounted.
 * Deactivates automatically on unmount.
 *
 * Use this with `scopeId` in shortcut registrations to isolate shortcuts
 * to a specific component instance (e.g. a specific open dialog).
 *
 * @param scopeId  Stable string ID for this scope instance.
 * @param active   Optional toggle — set to false to conditionally deactivate.
 *                 Useful when a component stays mounted but should not receive shortcuts.
 *
 * @example
 * // Dialog: activate a scope so only THIS dialog's shortcuts fire
 * function InvoiceDialog({ open }: { open: boolean }) {
 *   useShortcutScope('dialog:invoice', { active: open });
 *
 *   useRegisterShortcut({
 *     combo: { key: 'Escape' },
 *     scope: 'dialog',
 *     scopeId: 'dialog:invoice',  // ← ties to the scope above
 *     handler: closeDialog,
 *   });
 * }
 */
export function useShortcutScope(scopeId: string, options: { active?: boolean } = {}): void {
  const { active = true } = options;
  const { registry } = useShortcutManagerContext();

  useEffect(() => {
    if (!active) return;
    registry.activateScope(scopeId);
    return () => {
      registry.deactivateScope(scopeId);
    };
  }, [registry, scopeId, active]);
}

// ─────────────────────────────────────────────────────────────────────────────
// useShortcutEnabled
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Imperatively enable or disable a shortcut by its registered ID.
 * Unlike re-registration, this mutates the definition in-place.
 *
 * Useful for disabling shortcuts during loading / mutation states.
 *
 * @example
 * const id = useRegisterShortcut({ combo: { key: 's', modifiers: ['ctrl'] }, ... });
 * useShortcutEnabled(id, !isSubmitting);
 */
export function useShortcutEnabled(id: string, enabled: boolean): void {
  const { registry } = useShortcutManagerContext();

  useEffect(() => {
    registry.toggle(id, enabled);
    // On unmount, restore to enabled (registry.unregister handles full cleanup)
    return () => {
      registry.enable(id);
    };
  }, [registry, id, enabled]);
}

// ─────────────────────────────────────────────────────────────────────────────
// useShortcutContext  (convenience: scope + shortcuts together)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Convenience hook that activates a scope AND registers shortcuts for it
 * in a single call. The scope is active while the component is mounted.
 *
 * @example
 * useShortcutContext('form:customer-edit', [
 *   { combo: { key: 's', modifiers: ['ctrl'] }, scope: 'form', handler: save },
 *   { combo: { key: 'Escape' },                 scope: 'form', handler: cancel },
 * ]);
 */
export function useShortcutContext(
  scopeId: string,
  inputs: readonly Omit<ShortcutInput, 'scopeId'>[],
  deps: DependencyList = [],
): void {
  useShortcutScope(scopeId);

  // Inject scopeId into every input so they all belong to the scope
  const boundInputs: readonly ShortcutInput[] = inputs.map((input) => ({
    ...input,
    scopeId,
  }));

  useRegisterShortcuts(boundInputs, deps);
}

// ─────────────────────────────────────────────────────────────────────────────
// useShortcutHelp
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns all visible shortcuts (those with `meta` defined and `visible !== false`)
 * as a flat sorted list. Subscribes to registry changes via useSyncExternalStore.
 *
 * The returned array reference is STABLE between renders unless the registry
 * changes, preventing unnecessary re-renders in help overlay components.
 */
export function useShortcutHelp(): readonly ShortcutDefinition[] {
  const { registry } = useShortcutManagerContext();

  const subscribe = useCallback(
    (onStoreChange: () => void) => registry.subscribe(() => onStoreChange()),
    [registry],
  );

  return useSyncExternalStore(
    subscribe,
    () => registry.getVisibleShortcuts(),
    () => EMPTY_SHORTCUTS,
  );
}

/**
 * Like `useShortcutHelp` but returns shortcuts grouped by `meta.group`.
 * Useful for rendering a structured shortcut help overlay.
 */
export function useShortcutHelpGroups(): readonly ShortcutHelpGroup[] {
  const { registry } = useShortcutManagerContext();

  const subscribe = useCallback(
    (onStoreChange: () => void) => registry.subscribe(() => onStoreChange()),
    [registry],
  );

  return useSyncExternalStore(
    subscribe,
    () => registry.getVisibleShortcutGroups(),
    () => EMPTY_GROUPS,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// formatCombo  (utility — not a hook, safe to call outside React)
// ─────────────────────────────────────────────────────────────────────────────

const KEY_LABELS: Readonly<Record<string, string>> = {
  enter: '↵ Enter',
  escape: 'Esc',
  ' ': 'Space',
  tab: 'Tab',
  backspace: '⌫',
  delete: 'Del',
  arrowup: '↑',
  arrowdown: '↓',
  arrowleft: '←',
  arrowright: '→',
  pageup: 'PgUp',
  pagedown: 'PgDn',
  home: 'Home',
  end: 'End',
};

const isMacPlatform = (): boolean =>
  typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);

/**
 * Format a key combo as a human-readable string.
 * Adapts modifier labels for macOS vs Windows/Linux.
 *
 * @example
 * formatCombo({ key: 'k', modifiers: ['ctrl'] })
 * // → '⌘K' (macOS) or 'Ctrl+K' (Windows/Linux)
 *
 * formatCombo({ key: 'Enter' })
 * // → '↵ Enter'
 */
export function formatCombo(combo: {
  key: string;
  modifiers?: ModifierKey[] | readonly ModifierKey[];
}): string {
  const isMac = isMacPlatform();
  const mods = combo.modifiers ?? [];
  const parts: string[] = [];

  if (mods.includes('ctrl')) parts.push(isMac ? '⌘' : 'Ctrl');
  if (mods.includes('meta')) parts.push('⌘');
  if (mods.includes('alt')) parts.push(isMac ? '⌥' : 'Alt');
  if (mods.includes('shift')) parts.push(isMac ? '⇧' : 'Shift');

  const keyLabel = KEY_LABELS[combo.key.toLowerCase()] ?? combo.key.toUpperCase();
  parts.push(keyLabel);

  return isMac ? parts.join('') : parts.join('+');
}

/**
 * React hook version of `formatCombo`.
 * Prefer the plain `formatCombo` function unless you need reactivity.
 */
export function useFormatCombo(combo: { key: string; modifiers?: ModifierKey[] }): string {
  return formatCombo(combo);
}
